<template>
  <div class="installation-chard-container">
    <div class="header-container">
      <div class="header">
        TEAM PURCHASE AND INSTALL TARGETS
      </div>
      <div class="filter-container">
        <div v-if="selectedCorpId > 0" class="target-container">
          <button class="target-button flex-row" @click="toggleTargetDialog(!showTargetDialog)">
            <img src="@shared/assets/img/adoption/target-white.png" alt="Target" class="target-icon">
            <div class="sac__font-12 semi-bold">
              Goal
            </div>
          </button>
          <div v-if="showTargetDialog" class="target-popup-container">
            <div class="tp-header-container">
              <img src="@shared/assets/img/adoption/target-black.png" alt="Target" class="target-icon">
              <div class="tp-header">
                Weekly Target
              </div>
            </div>
            <div class="tp-body-container">
              <input v-model="installationCopy" class="tp-input" maxlength="2" @keypress="isNumber">
              <button class="tp-button" @click="saveInstallationTarget">
                <img src="@shared/assets/img/general/check-black.png" alt="Save" class="check-icon">
              </button>
              <button class="tp-button" @click="cancelInstallionTarget">
                <img src="@shared/assets/img/general/x-black.png" alt="Cancel" class="x-icon">
              </button>
            </div>
          </div>
        </div>
        <button :class="[
          'filter-button',
          activeFilter === '1month' ? 'active' : null,
        ]" @click="changeDateFilter('1month')">
          1 month
        </button>
        <button :class="[
          'filter-button',
          activeFilter === '3months' ? 'active' : null,
        ]" @click="changeDateFilter('3months')">
          3 months
        </button>
        <button :class="[
          'filter-button',
          activeFilter === '9months' ? 'active' : null,
        ]" @click="changeDateFilter('9months')">
          9 months
        </button>
        <Datepicker v-model="dateRange" mode="range" :max-date="new Date()" popover-align="center">
          <template #default>
            <button :class="[
              'filter-button',
              activeFilter === 'custom' ? 'active' : null,
            ]">
              <div v-if="activeFilter !== 'custom'">
                Custom
                <img src="@shared/assets/img/general/chevron-grey-down.png" alt="Select" class="button-icon">
              </div>
              <span v-else>
                {{ customRangeHeader }}
              </span>
              <div v-if="activeFilter === 'custom' && dateRange.start && dateRange.end" class="clear-container">
                <div class="clear-tooltip">
                  Clear Dates
                </div>
                <button class="clear-btn" @click="clearRangeFilter">
                  <img class="button-icon" src="@shared/assets/img/general/x-white.png" alt="Clear">
                </button>
              </div>
            </button>
          </template>
        </Datepicker>
      </div>
    </div>
    <div v-if="loading" class="loading-container">
      <img src="@shared/assets/img/general/loading-white.svg" alt="Loading" class="loader-img">
    </div>
    <highcharts v-else :options="chartOptions" />
    <div class="flex-row just-center">
      <div class="flex-row">
        <div class="color-gr" style="background-color: #50A26A;"></div>
        <div class="bold sac__font-12 f-white">Verified Installs</div>
      </div>
      <div class="flex-row ml-10 mr-10">
        <div class="color-gr" style="background-color: #397151;"></div>
        <div class="bold sac__font-12 f-white">Installs</div>
      </div>
      <div class="flex-row">
        <div class="color-gr" style="background-color: #2A448E;"></div>
        <div class="bold sac__font-12 f-white">Member Purchases</div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import Datepicker from 'v-calendar/lib/components/date-picker.umd';

import adoptionMixin from '@shared/mixins/adoptionMixin';
import unitPreferencesMixin from '@shared/mixins/unitPreferencesMixin';
import apiClient from '@shared/lib/general/apiClient';
import adminMixin from '@shared/mixins/adminMixin';

export default {
  name: 'InstallationChart',
  components: {
    Datepicker,
  },
  mixins: [adminMixin, adoptionMixin, unitPreferencesMixin],
  data() {
    return {
      activeFilter: '1month',
      showTargetDialog: false,
      installationTarget: null,
      installationCopy: null,
      graphData: [],
      customRange: {
        start: null,
        end: null,
      },
      loading: false,
    };
  },
  computed: {
    dateRange: {
      get() {
        return this.customRange;
      },
      set(dateRange) {
        if (dateRange && dateRange.start && dateRange.end) {
          this.customRange = dateRange;
          this.activeFilter = 'custom';
          this.getInstallGraph();
        }
      },
    },
    graphValues() {
      return this.graphData.map(({ date, count, purchaseCount, verifiedCount }) => {
        let color = 'rgba(102, 211, 131, 0.4)';
        if (this.installationTarget
          && this.installationTarget > count) {
          color = 'rgba(239, 91, 82, 0.4)';
        }
        return {
          date,
          count: count || 0,
          color,
          purchaseCount,
          verifiedCount,
        };
      });
    },
    xAxis() {
      return this.graphValues.map(({ date }) => date);
    },
    yAxisVerified() {
      return this.graphValues.map(({ verifiedCount, color }) => ({
        y: verifiedCount,
        color,
      }));
    },
    yAxisPurchase() {
      return this.graphValues.map(({ purchaseCount, color }) => ({
        y: purchaseCount,
        color: '#2a448e',
      }));
    },
    yAxis() {
      return this.graphValues.map(({ count, color }) => ({
        y: count,
        color,
      }));
    },
    chartOptions() {
      const {
        customRange,
        dateFormat,
        dateAndYearFormat,
      } = this;
      return {
        chart: {
          zoomType: 'x',
          height: 300,
          backgroundColor: 'transparent',
        },
        credits: {
          enabled: false,
        },
        title: {
          text: '',
        },
        tooltip: {
          useHTML: true,
          nullFormat: 'Value is not available.',
          formatter() {
            let date = '';
            if (customRange.start && customRange.end) {
              const {
                start,
                end,
              } = customRange;
              const mStart = moment(start);
              const mEnd = moment(end);
              const diff = mEnd.diff(mStart, 'days');
              if (diff < 7) {
                date = `${this.x}`;
              } else {
                let startDate = moment(this.x).startOf('week');
                let endDate = moment(this.x).endOf('week');
                if (startDate.isBefore(mStart)) {
                  startDate = mStart;
                }
                if (endDate.isAfter(mEnd)) {
                  endDate = mEnd;
                }
                const dateDiff = startDate.diff(endDate, 'days');
                if (dateDiff === 0) {
                  date = `${this.x}`;
                } else {
                  date = `${startDate.format(dateAndYearFormat)} to ${endDate.format(dateAndYearFormat)}`;
                }
              }
            } else {
              const startDate = moment(this.x).startOf('week').format(dateAndYearFormat);
              let endDate = moment(this.x).endOf('week').format(dateAndYearFormat);
              if (moment(this.x).endOf('week').isAfter(moment())) {
                endDate = moment().format(dateAndYearFormat);
              }
              date = `${startDate} to ${endDate}`;
            }
            return `${date}<br/>`
              + `<b>${this.y} unit${this.y > 1 ? 's' : ''}</b>`;
          },
          style: {
            fontFamily: 'Poppins',
          },
        },
        xAxis: {
          categories: this.xAxis,
          title: {
            text: '',
          },
          labels: {
            style: {
              color: 'white',
              fontFamily: 'Poppins',
            },
            formatter() {
              return moment(this.value).format(`${dateFormat}`);
            },
          },
          gridLineColor: '#121830',
        },
        yAxis: {
          labels: {
            style: {
              color: 'white',
              fontFamily: 'Poppins',
            },
          },
          title: {
            text: '',
          },
          min: Math.min(...this.yAxis.map(({ y }) => y)) <= 10
            ? 0 : Math.min(...this.yAxis.map(({ y }) => y)) - 10,
          max: Math.max(...this.yAxis.map(({ y }) => y), this.installationTarget) + 5,
          opposite: false,
          gridLineColor: '#121830',
          plotLines: [{
            value: this.installationTarget,
            //width: 2,
            dashStyle: 'dash',
            zIndex: 10,
            label: {
              useHTML: true,
              text: this.installationTarget,
              x: -35,
              y: 3,
              style: {
                color: '#006644',
                fontWeight: 700,
                backgroundColor: '#E3FCEF',
                padding: '3px 8px',
                borderTopLeftRadius: '10px',
                borderBottomLeftRadius: '10px',
                fontFamily: 'Poppins',
              },
            },
          }],
        },
        legend: {
          enabled: false,
        },
        plotOptions: {
          // column: {
          //   maxPointWidth: 32,
          // },
          scatter: {
            marker: {
              fillColor: '#0B0F1E',
              lineColor: '#FFFFFF',
              lineWidth: 3,
            },
          },
        },
        series: [

          {
            type: 'column',
            borderColor: 'transparent',
            showInLegend: false,
            name: "Installs",
            color: '#7C9CF5',
            pointPlacement: 0.09,
            linkedTo: 'main',
            color: 'rgba(102, 211, 131, 0.4)',
            data: this.yAxis,
          },
          {
            type: 'column',
            borderColor: 'transparent',
            showInLegend: false,
            color: '#2759E4',
            name: "Verified installs",
            pointPlacement: -0.11,
            linkedTo: 'main',
            data: this.yAxisVerified,
          },
          {
            type: 'column',
            borderColor: 'transparent',
            showInLegend: false,
            color: '#4979FF',
            name: "Has purchase",
            pointPlacement: -0.145,
            data: this.yAxisPurchase,
          }
        ],
      };
    },
    customRangeHeader() {
      const {
        start,
        end,
      } = this.customRange;

      const {
        dateFormat,
      } = this;

      if (start && end) {
        let startFormat = dateFormat === 'M/D' ? 'MMM D' : 'D MMM';
        if (!moment(start).isSame(end, 'year')) {
          startFormat = dateFormat === 'M/D' ? 'MMM D \'YY' : 'D MMM \'YY';
        }
        const endFormat = dateFormat === 'M/D' ? 'MMM D \'YY' : 'D MMM \'YY';
        const rangeStart = moment(start).format(startFormat);
        const rangeEnd = moment(end).format(endFormat);
        return `${rangeStart} - ${rangeEnd}`;
      }
      return null;
    },
  },
  watch: {
    activeFilter() {
      this.getInstallGraph();
    },
    selectedCorpId() {
      this.getInstallGraph();
      this.getInstallationTarget();
    },
  },
  mounted() {
    document.querySelector(':not(.target-popup-container)').addEventListener('click', this.closeOnClick);
    if (this.selectedCorpId) {
      this.getInstallGraph();
      this.getInstallationTarget();
    }
  },
  destroyed() {
    document.querySelector(':not(.target-popup-container)').removeEventListener('click', this.closeOnClick);
  },
  methods: {
    toggleTargetDialog(show) {
      this.showTargetDialog = show;
    },
    clearRangeFilter() {
      this.customRange = {
        start: null,
        end: null,
      };
      this.activeFilter = '1month';
    },
    closeOnClick(e) {
      if (!e.target.closest('.target-container') && !e.target.closest('.target-popup-container') && this.showTargetDialog) {
        this.showTargetDialog = false;
      }
    },
    async saveInstallationTarget() {
      try {
        const updateSettings = [{
          settingName: 'INSTALLATION_TARGET',
          settingValue: this.installationCopy,
          corpId: this.selectedCorpId,
        }];
        await apiClient({
          method: 'POST',
          url: '/partner/settings/set',
          data: updateSettings,
        });
        if (this.installationCopy) {
          this.$store.dispatch('showSuccessToast', {
            message: `Installation target was updated to ${this.installationCopy}`,
          });
        } else {
          this.$store.dispatch('showSuccessToast', {
            message: 'Installation target was updated',
          });
        }
        this.installationTarget = this.installationCopy;
        this.showTargetDialog = false;
      } catch (err) {
        console.error(err);
        this.$store.dispatch('showAlertToast', {
          message: 'There was an unexpected error updating installation target. Please try again or contact support',
        });
      }
    },
    cancelInstallionTarget() {
      this.installationCopy = this.installationTarget;
    },
    async getInstallGraph() {
      const {
        start,
        end,
      } = this.customRange;
      this.loading = true;
      try {
        let queryParams = '?';
        let startQuery = null;
        let endQuery = moment().format('YYYY-MM-DD');
        if (this.activeFilter === '1month') {
          startQuery = moment().subtract(1, 'month').format('YYYY-MM-DD');
        } else if (this.activeFilter === '3months') {
          startQuery = moment().subtract(3, 'month').format('YYYY-MM-DD');
        } else if (this.activeFilter === '9months') {
          startQuery = moment().subtract(9, 'month').format('YYYY-MM-DD');
        } else if (this.activeFilter === 'custom') {
          startQuery = moment(start).format('YYYY-MM-DD');
          endQuery = moment(end).format('YYYY-MM-DD');
        }
        if (startQuery) {
          queryParams += `start=${startQuery}&`;
        }
        queryParams += `end=${endQuery}`;
        let url = `/adoption/stat/install/corp/${this.selectedCorpId}`;
        url += queryParams;
        const { data } = await apiClient({
          method: 'GET',
          url,
        });
        this.graphData = data;
      } catch (err) {
        console.error(err);
      }
      this.loading = false;
    },
    async getInstallationTarget() {
      if (this.selectedCorpId > 0) {
        const { data: settings } = await apiClient({
          method: 'GET',
          url: `/partner/settings/${this.selectedCorpId}`,
        });
        if (settings.INSTALLATION_TARGET) {
          this.installationTarget = settings.INSTALLATION_TARGET;
          this.installationCopy = settings.INSTALLATION_TARGET;
        } else {
          this.installationTarget = null;
          this.installationCopy = null;
        }
      } else {
        this.installationTarget = null;
        this.installationCopy = null;
      }
    },
    isNumber(evt) {
      const event = evt || window.event;
      const charCode = (event.which) ? event.which : event.keyCode;
      if ((charCode > 31 && (charCode < 48 || charCode > 57)) && charCode !== 46) {
        event.preventDefault();
      } else {
        return true;
      }
      return true;
    },
    changeDateFilter(date) {
      this.activeFilter = date;
      if (date !== 'custom') {
        this.customRange = {
          start: null,
          end: null,
        };
      }
    },
  },
};
</script>

<style lang="scss">
.installation-chard-container {
  margin-top: 41px;
  display: flex;
  flex-direction: column;

  .header-container {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;

    .header {
      font-size: 0.875rem;
      font-weight: 700;
      color: $WHITE;
    }
  }

  .filter-container {
    display: flex;
    align-items: center;

    .target-container {
      position: relative;

      .target-button {
        padding: 5px 10px;
        display: flex;
        flex-direction: row;
        color: white;
        background-color: #4979FF;
        border-radius: 40px;
        align-items: center;

        .target-icon {
          width: 16px;
          height: 16px;
          object-fit: contain;
          margin-right: 5px;
        }
      }

      .target-popup-container {
        position: absolute;
        background-color: $N10;
        z-index: 2;
        min-width: 283px;
        border-radius: 8px;
        padding: 16px;
        left: -120px;

        .tp-header-container {
          display: flex;
          align-items: center;

          .target-icon {
            width: 16px;
            height: 16px;
            object-fit: contain;
            margin-right: 8px;
          }

          .tp-header {
            font-weight: 700;
            color: $DETAILBLACK;
          }
        }

        .tp-body-container {
          display: flex;
          align-items: center;
          margin-top: 18px;

          .tp-input {
            width: 40px;
            background-color: $E4;
            border-radius: 6px;
            height: 36px;
            padding-left: 10px;
            color: $WHITE;
            font-size: 1.125rem;
          }

          .tp-button {
            width: 24px;
            height: 24px;
            margin-left: 8px;

            .check-icon {
              width: 15px;
              height: 10px;
              object-fit: contain;
            }

            .x-icon {
              width: 11px;
              height: 11px;
              object-fit: contain;
            }
          }
        }
      }
    }

    .filter-button {
      font-size: 0.875rem;
      font-weight: 500;
      color: $N60;
      text-decoration: underline;
      margin-left: 24px;
      display: flex;
      align-items: center;

      &.active {
        font-weight: 700;
        color: $WHITE;
        text-decoration: none;
      }

      .button-icon {
        width: 12px;
        margin-left: 5px;
        object-fit: contain;
      }

      .clear-container {
        position: relative;

        &:hover {
          .clear-tooltip {
            opacity: 1;
          }
        }

        .clear-btn {
          display: flex;
        }

        .clear-tooltip {
          position: absolute;
          top: -20px;
          left: -26px;
          opacity: 0;
          background-color: $WHITE;
          color: $N100;
          font-size: 0.625rem;
          padding: 2px 4px;
          border-radius: 4px;
          white-space: nowrap;

          &:hover {
            opacity: 1;
          }
        }
      }
    }
  }

  .loading-container {
    height: 300px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
}

.color-gr {
  padding: 8px 15px;
  border-radius: 40px;
  border: white solid 1px;
  margin-right: 5px;
}
</style>
